/**
 * Iter 109 — Edición de rutas con PIN de supervisor.
 * - RutaEditPinDialog: pide el PIN y lo verifica contra el backend.
 * - AddLineasRutaDialog: añade líneas disponibles-para-rutar como paradas.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, Loader2, MapPin, Search } from "lucide-react";
import { ProyectoThumb } from "@/components/ProyectoThumb";

export function RutaEditPinDialog({ open, onOpenChange, onUnlocked }) {
  const [pin, setPin] = useState("");
  const [checking, setChecking] = useState(false);

  const verificar = async () => {
    if (!pin.trim()) return;
    setChecking(true);
    try {
      await api.post("/rutas/edit-pin/verificar", { pin: pin.trim() });
      toast.success("Edición de ruta desbloqueada");
      onUnlocked(pin.trim());
      setPin("");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN incorrecto");
    } finally { setChecking(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-400" /> PIN de edición de ruta
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Introduce el PIN de supervisor para añadir o quitar paradas no realizadas.
          </DialogDescription>
        </DialogHeader>
        <Input
          type="password" inputMode="numeric" autoFocus value={pin}
          onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") verificar(); }}
          placeholder="••••"
          className="bg-zinc-900 border-zinc-800 text-center text-lg tracking-[0.5em]"
          data-testid="ruta-edit-pin-input"
        />
        <DialogFooter>
          <Button onClick={verificar} disabled={checking || !pin.trim()}
            data-testid="ruta-edit-pin-submit"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold w-full">
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Desbloquear edición"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddLineasRutaDialog({ open, onOpenChange, rutaId, editPin, lineasEnRuta = [], onAdded }) {
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setSel(new Set());
    setQ("");
    setLoading(true);
    api.get("/lineas/disponibles-para-rutar")
      .then(({ data }) => setLineas((data || []).filter((l) => !lineasEnRuta.includes(l.id))))
      .catch(() => toast.error("Error cargando trabajos disponibles"))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line

  const toggle = (id) => setSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const filtradas = lineas.filter((l) => {
    if (!q.trim()) return true;
    const txt = `${l.descripcion || ""} ${l.peticion?.codigo || ""} ${l.peticion?.nombre || ""} ${l.cliente_nombre || ""} ${l.espacio?.nombre || ""} ${l.espacio?.ciudad || ""}`.toLowerCase();
    return txt.includes(q.trim().toLowerCase());
  });

  const guardar = async () => {
    if (sel.size === 0) return;
    setSaving(true);
    try {
      await api.post(`/rutas/${rutaId}/paradas-linea`, { linea_ids: [...sel] },
        { headers: { "X-Ruta-Edit-Pin": editPin } });
      toast.success(`${sel.size} parada(s) añadida(s) a la ruta`);
      onAdded && onAdded();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error añadiendo paradas");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>➕ Añadir líneas de trabajo a la ruta</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Trabajos con material listo para rutar. Se añadirán al final de la ruta.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por cliente, código, espacio, ciudad…"
            className="bg-zinc-900 border-zinc-800 pl-8 h-8 text-sm"
            data-testid="add-lineas-search" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1.5 pr-1" data-testid="add-lineas-list">
          {loading && <div className="text-sm text-zinc-500 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Cargando…</div>}
          {!loading && filtradas.length === 0 && (
            <div className="text-sm text-zinc-500 py-6 text-center">No hay trabajos disponibles para rutar.</div>
          )}
          {filtradas.map((l) => (
            <button key={l.id} type="button" onClick={() => toggle(l.id)}
              data-testid={`add-linea-${l.id}`}
              className={`w-full text-left rounded-lg border p-2.5 flex items-center gap-3 transition-colors ${
                sel.has(l.id) ? "border-emerald-500/60 bg-emerald-500/10" : "border-zinc-800 bg-zinc-900/40 hover:border-amber-500/40"}`}>
              <input type="checkbox" readOnly checked={sel.has(l.id)} className="accent-emerald-500 shrink-0" />
              <ProyectoThumb url={l.imagen_url || l.peticion?.imagen_url} nombre={l.cliente_nombre || "?"} size={34} rounded="lg" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-zinc-100 truncate">
                  <span className="text-amber-400 font-mono text-xs mr-1.5">{l.peticion?.codigo}</span>
                  {l.descripcion || l.peticion?.nombre}
                </div>
                <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
                  {l.cliente_nombre} · <MapPin className="w-3 h-3" /> {l.espacio?.nombre || "—"} {l.espacio?.ciudad ? `(${l.espacio.ciudad})` : ""}
                  <span className="text-zinc-600">· {l.num_componentes} comp.</span>
                </div>
              </div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400">Cancelar</Button>
          <Button onClick={guardar} disabled={saving || sel.size === 0}
            data-testid="add-lineas-confirm"
            className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Añadir ${sel.size || ""} parada(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
